import { uniq } from 'lodash-es'
import { MetadataFields } from '~/components/MetadataFields'
import Panel, { PanelSection } from '~/components/UIKit/Panel'
import LayerKeys from '~/config/layerKeys'
import ObjectClassName from '~/config/objectClassNames'
import { createAppSelector, useAppDispatch, useAppSelector } from '~/store/hooks'
import { updateSegments, updateWall } from '~/store/objects'
import { selectExteriorWallMinHeight } from '~/store/objects/selectors/walls'
import { DenormalizedWall, Wall, WallSegment } from '~/store/objects/types'
import { SELECTED_WALL_PANEL } from '~/store/panel/types'
import { mostRecentSelectedObjectOfClassName } from '~/store/selectedObjects/selectors'
import { Button } from '~/ui/Button'
import { Checkbox } from '~/ui/Checkbox'
import { Dialog } from '~/ui/Dialog'
import { DimensionInput } from '~/ui/DimensionInput'
import { Label } from '~/ui/Field'
import { Icon } from '~/ui/Icon'
import { DialogTrigger } from '~/ui/Modal'
import { Popover } from '~/ui/Popover'
import { Slider } from '~/ui/Slider'

const selectWallAndSegments = createAppSelector(
  [state => mostRecentSelectedObjectOfClassName(ObjectClassName.WALL, state), state => state.objects.present.segments],
  (selectedWall, segments): DenormalizedWall | null =>
    selectedWall
      ? {
          ...selectedWall,
          segments: selectedWall.segments.map(id => segments[id]),
        }
      : null
)

function uniqueBy<T, E>(items: T[], selector: (item: T) => E): E | null {
  const selected = uniq(items.map(selector))
  return selected.length === 1 ? selected[0] : null
}

export function SegmentsSection({ segments }: { segments: WallSegment[] }) {
  const dispatch = useAppDispatch()
  const interiorWallFullHeight = useAppSelector(selectExteriorWallMinHeight)

  const updateWallSegments = (props: Partial<WallSegment>) => {
    dispatch(
      updateSegments({
        ids: segments.map(segment => segment.id),
        updates: props,
      })
    )
  }

  const fullHeight = uniqueBy(segments, segment => segment.isFullHeight)
  const height = fullHeight ? interiorWallFullHeight : uniqueBy(segments, segment => segment.height)
  const thickness = uniqueBy(segments, segment => segment.thickness)
  const materialIndex = uniqueBy(segments, segment => segment.materialIndex ?? 0)
  const allInteriorWalls = segments.every(segment => segment.layerKey === LayerKeys.INTERIOR_WALLS)

  return (
    <PanelSection>
      <div className="flex flex-col space-y-3">
        <DimensionInput
          isDisabled={!!fullHeight && allInteriorWalls}
          value={height ?? NaN}
          placeholder="Multiple values"
          onCommit={newHeight => {
            updateWallSegments({ height: newHeight })
          }}
          showStepperButtons
        >
          <div className="flex flex-row items-center">
            <Label className="font-bold text-black text-xs">Height</Label>
            <DialogTrigger>
              <Button variant="icon" aria-label="Help">
                <Icon name="help" className="w-4 h-4" />
              </Button>
              <Popover className="max-w-[250px]">
                <Dialog>
                  <p className="text-sm">If the roof is sloped, enter eave height.</p>
                </Dialog>
              </Popover>
            </DialogTrigger>
          </div>
        </DimensionInput>
        {allInteriorWalls && (
          <Checkbox
            isIndeterminate={fullHeight === null}
            isSelected={!!fullHeight}
            onChange={newFullHeight => {
              updateWallSegments({ isFullHeight: newFullHeight })
            }}
          >
            Full Height
          </Checkbox>
        )}
        <DimensionInput
          label="Thickness"
          value={thickness ?? NaN}
          placeholder="Multiple values"
          onCommit={newThickness => {
            updateWallSegments({ thickness: newThickness })
          }}
          showStepperButtons
        />
        {(height === null || thickness === null) && (
          <div className="bg-blue-500/25 border border-blue-500 rounded p-2">
            <span className="pr-1">
              <Icon name="info" className="inline text-blue-500" />
            </span>
            Multiple walls are selected. Changes will apply to each selected wall segment.
          </div>
        )}
        {materialIndex !== null && (
          <Slider
            defaultValue={1 - materialIndex / 10}
            onChangeEnd={value => updateWallSegments({ materialIndex: (1 - value) * 10 })}
            minValue={0}
            maxValue={1}
            step={1 / 10}
            label="Opacity"
          />
        )}
      </div>
    </PanelSection>
  )
}

export default function SelectedWallPanel({ alignment = 'right' }: { alignment?: string }) {
  const wall = useAppSelector(selectWallAndSegments)
  const normalizedWall: Wall | null = wall ? { ...wall, segments: wall.segments.map(({ id }) => id) } : null
  const isTouchUI = useAppSelector(state => state.userInterface.isTouchUI)
  const dispatch = useAppDispatch()

  return (
    <Panel title="Walls" alignment={alignment} scrollable docked panelKey={SELECTED_WALL_PANEL} hasToolbar={isTouchUI}>
      {wall && <SegmentsSection segments={wall.segments} />}
      {normalizedWall && (
        <MetadataFields
          onChange={(fieldName, newValue) => {
            dispatch(
              updateWall({
                wall: { ...normalizedWall, metadata: { ...normalizedWall.metadata, [fieldName]: newValue } },
              })
            )
          }}
          onDeleteImage={updatedWall => dispatch(updateWall({ wall: updatedWall }))}
          selectedObjects={[normalizedWall]}
        />
      )}
    </Panel>
  )
}
